import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Updates the native quantity input from the compact cart-page select.
   * The inherited change event keeps the existing AJAX cart update flow intact.
   * @param {Event} event
   */
  setQuantityFromSelect(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    this.refs.quantityInput.value = event.target.value;
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Routes the custom cart quantity menu through the existing Shopify quantity update flow.
   * @param {Event} event
   */
  setQuantityFromCompactOption(event) {
    const option = event.target instanceof Element ? event.target.closest('[data-quantity]') : null;
    if (!(option instanceof HTMLButtonElement)) return;

    const quantitySelect = /** @type {HTMLSelectElement | undefined} */ (this.refs.quantitySelect);
    if (!quantitySelect) return;

    quantitySelect.value = option.dataset.quantity ?? quantitySelect.value;
    this.refs.quantityInput.value = quantitySelect.value;
    this.onQuantityChange();
    this.updateButtonStates();

    const quantityValue = /** @type {HTMLElement | undefined} */ (this.refs.compactQuantityValue);
    if (quantityValue) quantityValue.textContent = quantitySelect.value;

    const quantityDetails = /** @type {HTMLDetailsElement | undefined} */ (this.refs.compactQuantityDetails);
    if (quantityDetails) quantityDetails.open = false;
  }

  updateCartQuantity() {
    super.updateCartQuantity();

    const quantitySelect = /** @type {HTMLSelectElement | undefined} */ (this.refs.quantitySelect);
    if (quantitySelect) quantitySelect.value = this.refs.quantityInput.value;

    const quantityValue = /** @type {HTMLElement | undefined} */ (this.refs.compactQuantityValue);
    if (quantityValue) quantityValue.textContent = this.refs.quantityInput.value;
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
